import { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import useCustomShortcode, { useCustomShortcodeForm } from '@hooks/useCustomShortcode'

const confirmDelete = (deleteCustomShortcode, deleteFn, closeModal) => {
  const deleteCustomShortcodeModal = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'Custom Shortcode Deleted',
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Yes, delete it.',
      icon: 'warning',
      showCancelButton: true,
      text: 'Are you sure? This could affect artwork population.',
      title: 'Delete Custom Shortcode?',
    })
    .then(({ value }) => {
      if (value){
        deleteCustomShortcode(deleteCustomShortcodeModal)
      }
    })
}

const createOrUpdateCustomShortcode = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createCustomShortcode, updateCustomShortcode },
  } = props

  const toastText = entityState.id ? 'Custom Shortcode Updated' : 'Custom Shortcode Created'
  const actionFn = entityState.id ? updateFn : createFn

  const shortcodePayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText,
  }

  if (entityState.id) return updateCustomShortcode(shortcodePayload)

  return createCustomShortcode(shortcodePayload)
}

const CreateOrEditCustomShortcodeModal = (props) => {
  const { callbacks, modalKey = 'CreateOrEditCustomShortcodeModal', showModal } = props
  const { closeModal, deleteCustomShortcode } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { customShortcode: initCustomShortcode } = modalPayload

  const {
    callbacks: {
      createCustomShortcode: createFn,
      deleteCustomShortcode: deleteFn,
      updateCustomShortcode: updateFn,
    },
    creating,
    customShortcode,
    deleting,
    updating,
  } = useCustomShortcode(initCustomShortcode)

  const {
    entityState,
    entityState: {
      id,
      key,
      title,
      value,
    },
    errors,
    handlers,
    saveEnabled,
  } = useCustomShortcodeForm(customShortcode, { validateOn: 'change' })

  const saveOrCreateButtonText = id ? 'Save Shortcode' : 'Create Shortcode'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Create'} titleSecondLine="Shortcode" />

      <SidebarModal.Content>
        <Form>
          <Form.Field
            errorMessage={errors.title}
            label="* Title"
          >
            <input name="title" value={title} type="text" {...handlers} />
          </Form.Field>

          <Form.Field
            errorMessage={errors.key}
            label="* Shortcode"
            marginTop="large"
          >
            <input name="key" value={key} type="text" {...handlers} />
          </Form.Field>

          <Form.Field
            errorMessage={errors.value}
            label="* Value"
            marginTop="large"
          >
            <input name="value" value={value} type="text" {...handlers} />
          </Form.Field>

          {id && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(deleteCustomShortcode, deleteFn, closeModal)}
                size="medium"
              >
                Delete Custom Shortcode
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateCustomShortcode(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditCustomShortcodeModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCustomShortcodeModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
